﻿/* https://brandplatform.smartphoto.com/general/popups/ai-multiple-filters/ */
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { showLoadingDialog } from "./loading-dialog";
import { createPageLog, IPageLog, PageLogStyle } from "../molecules/page-log";
import { createPopup } from "../technical/popup-new";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	hasClass,
} from "../../common/html";
import { IPhoto, PhotoSourcesPopup } from "../organisms/photo-sources-popup";
import { getParameter, updateParameter } from "../../utils/querystring";
import { initDisableContextMenu } from "../technical/disable-context-menu";
import { PhotoImport, sendImportPhotosFinishedCreatorEvent } from "../technical/creator-events";
import { SmartphotoPhoto, StoreType } from "../../services/storageService";

export interface IAiMultipleFiltersDependencies {
	close?: () => void;
	error?: (message: string) => void;
	selectedFilterQuery?: string;
	embed?: boolean;
}

export function createAiMultipleFilters(
	deps: IAiMultipleFiltersDependencies,
	pvc: string,
	design: string,
	theme?: string,
	quantityString?: string,
) {
	enum QueueStatus {
		Undefined,
		Pending,
		Generated,
		Downloading,
		Followup,
		Done,
		Error,
	}

	const buttonDisabledClass = "a-button--disabled";
	const activeFilterClass = "o-ai-multiple-filters__filter-container--active";
	const activeHistoryClass = "o-ai-multiple-filters__history-container--active";

	const eventDisposables: IDisposable = compositeDisposable(undefined);
	let hasCurrentCreations = false;
	let attemptsLeft = 0;
	const activePollers: NodeJS.Timeout[] = [];

	const updateNextStepUrl = (popupEl: HTMLElement) => {
		const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);
		const allowMultipleUpload = uploadButton.dataset.allowMultiple === "true";

		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
		continueButtonEls.forEach((continueButton) => {
			let updatedHref = continueButton.getAttribute("href");
			// Add all the history photos as a selection to the user
			const historyEls = expressQuerySelectorAll<HTMLElement>(
				popupEl,
				".technical-aifilter-history[data-photoid]:not(.technical-aifilter-history-template)",
			);
			// only select the photoid's from the history that are already loaded
			const selectionIds = historyEls.filter((x) => x.dataset.photourl).map((x) => x.dataset.photoid);
			const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
			const currentPhotoId = mainPhoto.dataset.photoId;
			updatedHref = updateParameter(
				"photoids",
				allowMultipleUpload
					? currentPhotoId + "," + selectionIds.filter((x) => x !== currentPhotoId).join(",")
					: currentPhotoId, // current photoid in front -> always filled as the first, then sorted by date created
				updatedHref,
			);

			if (theme) updatedHref = updateParameter("theme", theme, updatedHref);

			updatedHref = updateParameter("selectionids", selectionIds.join(","), updatedHref);
			continueButton.setAttribute("href", updatedHref);
			continueButton.classList.toggle(
				buttonDisabledClass,
				!historyEls.find((x) => x.dataset.photourl && x.dataset.photoid == currentPhotoId),
			);
		});
	};

	const swapPhoto = (popupEl: HTMLElement, photoId: string, photoUrl: string) => {
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
		const emptyStateEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-emptystate", true);
		const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", true);
		mainPhoto.src = photoUrl;
		mainPhoto.removeAttribute("data-pendingid");
		mainPhoto.dataset.photoId = photoId;
		expressRemoveClass(mainPhoto, "u-hide");
		expressAddClass(emptyStateEl, "u-hide");

		updateNextStepUrl(popupEl);

		loading.classList.add("u-hide");
	};

	// This method shows an error because something went wrong
	const showError = (
		popupEl: HTMLElement,
		pageLogComponent: IPageLog,
		pendingId: string,
		errorMessage: string,
		filterNumber: string,
		attemptsText?: string,
	) => {
		if (!errorMessage) throw new Error("pendingError called without errorMessage");
		const matchingFilter = expressQuerySelector<HTMLElement>(
			popupEl,
			`.technical-aifilter-filter[data-filternumber='${filterNumber}']`,
			false,
		);
		const matchingFilterTitleEl =
			matchingFilter && expressQuerySelector<HTMLElement>(matchingFilter, ".technical-filter-name", false);

		// reset main photo
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
		if (mainPhoto.dataset.pendingid == pendingId || mainPhoto.dataset.pendingid === "null") {
			mainPhoto.removeAttribute("data-pendingid");
			const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", false);
			loading.classList.add("u-hide");
		}

		// reset history
		const historyEl = expressQuerySelector<HTMLImageElement>(
			popupEl,
			`.technical-aifilter-history[data-filternumber="${filterNumber}"]`,
			false,
		);
		historyEl && historyEl.remove();

		// reset selected filter
		const selectedFilter = expressQuerySelector(
			popupEl,
			`.technical-aifilter-filter[data-filternumber="${filterNumber}"]`,
			false,
		);
		selectedFilter && selectedFilter.classList.remove("o-ai-multiple-filters__filter-container--active");

		// set pagelog
		pageLogComponent.updateMessageAndStyle(
			PageLogStyle.Error,
			errorMessage.replace("{0}", matchingFilterTitleEl?.innerText || ""),
			false,
		);
	};

	// This method will poll the server for a pending photo in the history
	const pollForPending = async (popupEl: HTMLElement, pageLogComponent: IPageLog, pendingId: string) => {
		fetch("/apicore/aifilter/aipoll", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ id: pendingId }),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data && data.status === QueueStatus.Followup) {
					// Followup means the AI task was done, but a new one was started. Example VertexAI first, then RemoveBackground.
					// In this case, we want to update the pendingId with the new one and keep polling until we get a "done" status.
					// Change all pending IDs in the UI to the new one
					const historyEl = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-partnerid='${pendingId}']`,
						false,
					);
					if (historyEl) historyEl.dataset.partnerid = data.newPendingId;

					const mainPhoto = expressQuerySelector<HTMLImageElement>(
						popupEl,
						`.technical-aifilter-main[data-pendingid='${pendingId}']`,
						false,
					);
					if (mainPhoto) mainPhoto.dataset.pendingid = data.newPendingId;

					// start polling on the new ID
					const timeoutId = setTimeout(() => {
						activePollers.splice(activePollers.indexOf(timeoutId), 1);
						pollForPending(popupEl, pageLogComponent, data.newPendingId);
					}, 1500);
					activePollers.push(timeoutId);
				} else if (data && data.status === QueueStatus.Done) {
					// When done, show the photo in the history
					const historyEl = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-partnerid='${pendingId}']`,
						false,
					);
					if (historyEl) {
						const loadingEl = expressQuerySelector<HTMLElement>(historyEl, ".technical-photo-loading", true);
						const historyImage = expressQuerySelector<HTMLImageElement>(historyEl, "img", true);
						historyImage.classList.remove("u-hide");
						historyImage.src = data.url;
						historyEl.dataset.photoid = data.id;
						historyEl.dataset.photourl = data.url;
						loadingEl.classList.add("u-hide");
					}

					// If the main photo is waiting for this pending, swap it + change the CTA URL
					const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", false);
					if (mainPhoto.dataset.pendingid == pendingId) swapPhoto(popupEl, data.id, data.url);
					else updateNextStepUrl(popupEl);

					const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);
					if (attemptsLeft === 0) {
						expressAddClass(uploadButton, buttonDisabledClass);
					} else {
						expressRemoveClass(uploadButton, buttonDisabledClass);
					}
				} else if (data && data.errorMessage) {
					const matchingHistoryPhoto = expressQuerySelector<HTMLElement>(
						popupEl,
						`.technical-aifilter-history[data-photoid='${pendingId}']`,
						false,
					);
					showError(
						popupEl,
						pageLogComponent,
						pendingId,
						data.errorMessage,
						matchingHistoryPhoto?.dataset.filternumber,
						data.attemptsLabel,
					);
				} else {
					const timeoutId = setTimeout(() => {
						activePollers.splice(activePollers.indexOf(timeoutId), 1);
						pollForPending(popupEl, pageLogComponent, pendingId);
					}, 1500);
					activePollers.push(timeoutId);
				}
			});
	};

	const showStartedTaskAndPoll = async (
		popupEl: HTMLElement,
		filterGroupNumber: number,
		photoId: string,
		pendingId: string,
		pageLogComponent: IPageLog,
		count: number,
	) => {
		const mainPhoto = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main", true);
		const loading = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-main-loading", true);

		/* History photos */
		// History: Make a copy of the template
		const historyEl = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-history-template", true);
		const newHistoryEl = historyEl.cloneNode(true) as HTMLImageElement;

		// History: Remove active class of other history items + set as active only if it is active on the main photo
		if (mainPhoto.dataset.originalPhotoId === photoId) {
			// Change the main picture to the original one
			mainPhoto.setAttribute("data-pendingid", pendingId);
			// mainPhoto.src = activePhotoUrl;
			loading.classList.remove("u-hide");

			deselectActiveHistory(popupEl);
			newHistoryEl.classList.add(activeHistoryClass);
		}

		// History: Set attributes of the newly created history div
		newHistoryEl.dataset.filternumber = filterGroupNumber.toString();
		newHistoryEl.classList.remove("u-hide");
		newHistoryEl.classList.remove("technical-aifilter-history-template");
		newHistoryEl.removeAttribute("data-photoid");
		newHistoryEl.removeAttribute("data-photourl");
		newHistoryEl.dataset.originalphotoid = photoId || "";
		newHistoryEl.dataset.partnerid = pendingId;

		// History: Set the image to loading of the new history element
		const historyImage = expressQuerySelector<HTMLImageElement>(newHistoryEl, "img", true);
		historyImage.classList.add("u-hide");
		const loadingEl = expressQuerySelector<HTMLElement>(newHistoryEl, ".technical-photo-loading", true);
		loadingEl.classList.remove("u-hide");

		// History: Add the item to the DOM
		const historyContainer = historyEl.parentElement;
		if (historyContainer) historyContainer.insertBefore(newHistoryEl, historyContainer.firstChild);

		// History: Add a divider between past creations and current ones
		const divider = expressQuerySelector<HTMLImageElement>(popupEl, ".technical-aifilter-history-divider", false);
		if (divider) divider.classList.remove("u-hide");

		return new Promise<void>((resolve, reject) => {
			pollForPending(popupEl, pageLogComponent, pendingId);
		});
	};

	const startTask = async (
		popupEl: HTMLElement,
		aiFilters: HTMLElement[],
		pageLog: HTMLElement,
		pageLogComponent: IPageLog,
		photoIds: string[],
		filterGroupNumber: number,
	) => {
		const uploadButton = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-upload", true);

		// Check if we already clicked this filter before. If yes? Select it from the history
		const existingHistory = expressQuerySelector<HTMLImageElement>(
			popupEl,
			".technical-aifilter-history[data-filterNumber='" +
				filterGroupNumber +
				"'][data-originalphotoid='" +
				photoIds[0] +
				"']",
			false,
		);

		if (pageLog.dataset.filterNumber === filterGroupNumber.toString()) pageLogComponent.hideLog();

		if (existingHistory) {
			existingHistory.click();
			return;
		}

		expressAddClass(uploadButton, buttonDisabledClass);

		// Check if we have 0 attempts left, if so, block action (visual cue needed? Not yet)
		if (attemptsLeft === 0) return;

		hasCurrentCreations = true;

		// Set the current filter active - if we have any
		aiFilters.forEach((aiFilter) => {
			if (aiFilter.dataset.filternumber === filterGroupNumber.toString()) aiFilter.classList.add(activeFilterClass);
			else aiFilter.classList.remove(activeFilterClass);
		});

		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(popupEl, ".technical-aifilter-continue");
		continueButtonEls.forEach((continueButton) => (continueButton.textContent = continueButton.dataset.labelcontinue));

		fetch("/apicore/aifilter/starttask", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				originalPhotoIds: photoIds,
				pvc,
				chosenFilterGroupNumber: filterGroupNumber,
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				const pendingId = data.pendingIds[0] || null;

				// error during start of task
				if (data.errorMessage) {
					showError(
						popupEl,
						pageLogComponent,
						pendingId,
						data.errorMessage,
						filterGroupNumber.toString(),
						data.attemptsLabel,
					);
					if (!pendingId) return;
				}

				const attemptsLabelEl = expressQuerySelector<HTMLElement>(popupEl, ".technical-aifilter-attempts", false);
				updatePendingLabels(attemptsLabelEl, data.label);

				// Disable the buttons
				continueButtonEls.forEach(
					(continueButton) => continueButton.href && continueButton.classList.add(buttonDisabledClass),
				);
				expressAddClass(uploadButton, buttonDisabledClass);

				const promises = [];
				for (let i = 0; i < data.pendingIds.length; i++) {
					promises.push(
						showStartedTaskAndPoll(popupEl, filterGroupNumber, photoIds[i], data.pendingIds[i], pageLogComponent, i),
					);
				}

				return Promise.all(promises);
			});
	};

	const updatePendingLabels = (attemptsLabelEl: HTMLElement, attemptsText?: string) => {
		// Update the # attempts label
		if (attemptsText && attemptsLabelEl) {
			attemptsLabelEl.textContent = attemptsText;
			attemptsLabelEl.classList.remove("u-hide");
		}
	};

	const deselectActiveFilters = (popupEl: HTMLElement) => {
		const activeFilters = expressQuerySelectorAll<HTMLElement>(
			popupEl,
			".technical-aifilter-filter." + activeFilterClass,
		);
		activeFilters.forEach((activeFilter) => activeFilter.classList.remove(activeFilterClass));
	};

	const deselectActiveHistory = (popupEl: HTMLElement) => {
		const activeHistory = expressQuerySelectorAll<HTMLElement>(
			popupEl,
			".technical-aifilter-history." + activeHistoryClass,
		);
		activeHistory.forEach((activeHistory) => activeHistory.classList.remove(activeHistoryClass));
	};

	const deselectActiveFiltersAndHistory = (popupEl: HTMLElement) => {
		deselectActiveFilters(popupEl);
		deselectActiveHistory(popupEl);
	};

	const onInit = (containerEl: HTMLElement) => {
		// Set attempts left (attribute on the popup)
		attemptsLeft = parseInt(containerEl.dataset.attemptsLeft) || 0;

		// Load the main picture if it is pending
		const pageLog = expressQuerySelector<HTMLElement>(containerEl, ".technical-top-message-log", true);
		const pageLogComponent = createPageLog(pageLog);
		const mainPhoto = expressQuerySelector<HTMLImageElement>(containerEl, ".technical-aifilter-main", false);
		const emptyStateEl = expressQuerySelector<HTMLElement>(containerEl, ".technical-emptystate", false);
		const continueButtonEls = expressQuerySelectorAll<HTMLLinkElement>(containerEl, ".technical-aifilter-continue");
		const uploadButton = expressQuerySelector<HTMLElement>(containerEl, ".technical-aifilter-upload", true);

		enum ContinueButtonLabel {
			Continue,
			SelectFilter,
			SelectGenerated,
		}

		if (mainPhoto) {
			let activePhotoId = mainPhoto.dataset.originalPhotoId;
			let activePhotoUrl = mainPhoto.src;

			// Add upload button functionality
			const initPhotoUpload = () => {
				if (hasClass(uploadButton, buttonDisabledClass)) return;
				pageLogComponent.hideLog(); // reset page log

				const allowMultipleUpload = uploadButton.dataset.allowMultiple === "true";

				const onImportPhotosFinished = async (photos: IPhoto[]): Promise<void> => {
					if (!photos || photos.length === 0) {
						return;
					}

					expressRemoveClass(mainPhoto, "u-hide");
					expressAddClass(emptyStateEl, "u-hide");

					mainPhoto.src = photos[0].mediumUrl;
					mainPhoto.dataset.originalPhotoId = photos[0].fileId;
					activePhotoId = photos[0].fileId;

					activePhotoUrl = mainPhoto.src;
					uploadButton.textContent = uploadButton.dataset.labeluploadanother;
					updateContinueButtonLabels(ContinueButtonLabel.SelectFilter);
					deselectActiveFiltersAndHistory(containerEl);

					if (mainPhoto.dataset.filterCount === "1") {
						// We only have 1 filter available -> just apply it
						const aiFilters = expressQuerySelectorAll<HTMLElement>(
							containerEl,
							".technical-aifilter-filter:not(.o-ai-multiple-filters__container-forbidden)",
						);
						mainPhoto.src = photos[0].mediumUrl;
						mainPhoto.dataset.originalPhotoId = photos[0].fileId;
						startTask(
							containerEl,
							aiFilters,
							pageLog,
							pageLogComponent,
							photos.map((p) => p.fileId),
							0,
						);
					} else {
						mainPhoto.src = photos[0].mediumUrl;
						mainPhoto.dataset.originalPhotoId = photos[0].fileId;
						const disabledAiFilters = expressQuerySelectorAll<HTMLElement>(
							containerEl,
							".o-ai-multiple-filters__container-forbidden",
						);
						disabledAiFilters.forEach((filter) =>
							expressRemoveClass(filter, "o-ai-multiple-filters__container-forbidden"),
						);
						enableFilters();
					}
				};

				// disable the button
				expressAddClass(uploadButton, buttonDisabledClass);

				const photoSources = new PhotoSourcesPopup(onImportPhotosFinished, allowMultipleUpload ? 99 : 1);
				photoSources.openPhotoStoresPopup({
					onBeforeClose: () => {
						expressRemoveClass(uploadButton, buttonDisabledClass);
					},
				});
			};

			if (uploadButton) {
				eventDisposables.add(expressEventListener(uploadButton, "click", () => initPhotoUpload()));
			}

			// Enable the history clicks
			const historyContainer = expressQuerySelector<HTMLElement>(
				containerEl,
				".technical-aifilter-history-container",
				false,
			);
			if (historyContainer) {
				// Add polling for the pending images
				const pendingHistory = expressQuerySelectorAll<HTMLLinkElement>(
					historyContainer,
					".technical-aifilter-history[data-partnerid]",
				);
				pendingHistory.forEach((historyItem) => {
					const pendingId = historyItem.dataset.partnerid;
					pollForPending(containerEl, pageLogComponent, pendingId);
				});

				// Add click to swap the foto to the one from the history
				eventDisposables.add(
					expressEventListener(historyContainer, "click", (e) => {
						const historyDiv = e.target.closest(".technical-aifilter-history");
						if (historyDiv && historyDiv.dataset.photoid) {
							swapPhoto(containerEl, historyDiv.dataset.photoid, historyDiv.dataset.photourl);
							deselectActiveHistory(containerEl);

							// Select the clicked history as being active
							historyDiv.classList.add(activeHistoryClass);

							// Match the clicked history with the clicked filter
							deselectActiveFilters(containerEl);
							const matchingFilter = expressQuerySelector<HTMLImageElement>(
								containerEl,
								".technical-aifilter-filter[data-filterNumber='" + historyDiv.dataset.filternumber + "']",
								false,
							);
							if (matchingFilter && historyDiv.dataset.originalphotoid === mainPhoto.dataset.originalPhotoId)
								matchingFilter.classList.add(activeFilterClass);

							// Add a divider between past creations and current ones
							if (hasCurrentCreations) {
								const divider = expressQuerySelector<HTMLImageElement>(
									containerEl,
									".technical-aifilter-history-divider",
									false,
								);
								if (divider) divider.classList.remove("u-hide");
							}

							updateContinueButtonLabels(ContinueButtonLabel.Continue);
						}
					}),
				);
			}

			const updateContinueButtonLabels = (label: ContinueButtonLabel) => {
				// Update the label of the buttons
				continueButtonEls.forEach((continueButton) => {
					if (label === ContinueButtonLabel.Continue) continueButton.textContent = continueButton.dataset.labelcontinue;
					if (label === ContinueButtonLabel.SelectFilter) {
						continueButton.textContent = continueButton.dataset.labelselectfilter;
						// disable the continue button => first a filter or history item needs to be selected to continue to the CRE
						continueButton.classList.add(buttonDisabledClass);
					}
					if (label === ContinueButtonLabel.SelectGenerated)
						continueButton.textContent = continueButton.dataset.labelselectgenerated;
				});
			};

			// Enable the filter clicks
			const enableFilters = () => {
				const aiFilters = expressQuerySelectorAll<HTMLElement>(
					containerEl,
					".technical-aifilter-filter:not(.o-ai-multiple-filters__container-forbidden)",
				);
				aiFilters.forEach((aiFilter) => {
					eventDisposables.add(
						expressEventListener(aiFilter, "click", () => {
							startTask(
								containerEl,
								aiFilters,
								pageLog,
								pageLogComponent,
								[activePhotoId],
								parseInt(aiFilter.dataset.filternumber) || 0,
							);
						}),
					);
				});
			};

			enableFilters();

			const pendingId = mainPhoto.dataset.pendingid || "";
			if (pendingId && pendingId !== "") {
				return new Promise<void>((resolve, reject) => {
					pollForPending(containerEl, pageLogComponent, pendingId);
				});
			}

			const continueBtns = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-aifilter-continue");
			continueBtns.forEach((continueBtn) => {
				eventDisposables.add(
					expressEventListener(continueBtn, "click", (e: MouseEvent) => {
						if (continueBtn.classList.contains(buttonDisabledClass)) {
							e.preventDefault();
							return;
						}
						showLoadingDialog(Promise.resolve(), 0, 10000);
						if (!deps.embed) return;
						e.preventDefault();
						const url = continueBtn.getAttribute("href");
						if (!url) return;
						const photoIds = getParameter("photoids", url);
						if (!photoIds) return;
						const photoIdArray = photoIds.split(",");
						const bc = new BroadcastChannel("connectedToStorePartner");
						const photos: SmartphotoPhoto[] = [];
						photoIdArray.forEach((photoId) => {
							photos.push({ fileId: photoId } as SmartphotoPhoto);
						});
						sendImportPhotosFinishedCreatorEvent(bc, {
							selection: photos,
							storeType: StoreType.eXpress,
						} as PhotoImport);
					}),
				);
			});
		}
	};

	const initEmbed = async () => {
		const containerEl = expressQuerySelector<HTMLElement>(document, ".technical-ai-filter-page", true);
		await onInit(containerEl);
		initDisableContextMenu(containerEl);
	};

	const init = async () => {
		const aiPhotoPopup = await showLoadingDialog(
			createPopup({
				dialogId: "showaiphotopopup_" + pvc + "_" + design,
				view: "PhotoWithAiFilter",
				closeOnClickOutsidePopup: false,
				onClose: () => {
					// Stop all active pollers
					activePollers.forEach((poller) => clearTimeout(poller));
					deps.close();
				},
				onBeforeOpen: (popupEl: HTMLElement) => onInit(popupEl),
				forceRefresh: true, // refresh to reset api errors in popup
				data: {
					Pvc: pvc,
					Design: design,
					QuantityString: quantityString,
				},
			}),
		);

		if (aiPhotoPopup.popup.dataset.hasError) {
			if (aiPhotoPopup.popup.dataset.showOnPagePagelog) {
				deps.error && deps.error(aiPhotoPopup.popup.dataset.errorMessage);
				return;
			}
			// something went wrong => open select photo dialog again
			return Promise.reject(aiPhotoPopup.popup.dataset.errorMessage);
		}

		await aiPhotoPopup.openAsync();

		initDisableContextMenu(aiPhotoPopup.popup);
	};

	deps.embed ? initEmbed() : init();

	return {
		dispose: () => {
			eventDisposables.dispose();
		},
	};
}
